import React, { useState } from "react";
import { Box, Button, Menu, MenuItem, Typography } from "@mui/material";
import dayjs from "dayjs";
import calendar from "packages/novous_web/src/assets/icons/calendar.svg";
import arrow from "packages/novous_web/src/assets/icons/Arrow.svg";
import DateRangeFilter from "packages/novous_web/src/common/components/DateRange";

const dateRangeArr: string[] = [
  "Today",
  "Yesterday",
  "7 Days",
  "15 Days",
  "30 Days",
  "Custom Range",
];

const getDateRange = (range: string, addTime?: boolean) => {
  const today = dayjs();
  let startDate, endDate;
  let fromTime, toTime;
  switch (range) {
    case "Today":
      startDate = today.format("YYYY-MM-DD");
      endDate = today.format("YYYY-MM-DD");
      if (addTime) {
        fromTime = today.startOf("day").format("HH:mm:ss");
        toTime = today.endOf("day").format("HH:mm:ss");
      }
      break;
    case "Yesterday":
      startDate = dayjs().subtract(1, "day").format("YYYY-MM-DD");
      endDate = startDate;
      if (addTime) {
        fromTime = "00:00:00";
        toTime = "23:59:59";

      }
      break;
    case "7 Days":
      startDate = dayjs().subtract(6, "day").format("YYYY-MM-DD");
      endDate = today.format("YYYY-MM-DD");
      if (addTime) {
        fromTime = "00:00:00";
        toTime = "23:59:59";
      }
      break;
    case "15 Days":
      startDate = dayjs().subtract(14, "day").format("YYYY-MM-DD");
      endDate = today.format("YYYY-MM-DD");
      if (addTime) {
        fromTime = "00:00:00";
        toTime = "23:59:59";
      }
      break;
    case "30 Days":
      startDate = dayjs().subtract(29, "day").format("YYYY-MM-DD");
      endDate = today.format("YYYY-MM-DD");
      if (addTime) {
        fromTime = "00:00:00";
        toTime = "23:59:59";
      }
      break;
    default:
      startDate = "";
      endDate = "";
      if (addTime) {
        fromTime = "";
        toTime = "";
      }
  }
  return { startDate, endDate };
};

type Props = {
  onDateRangeChange?: (from: string, to: string, fromTime?: string, toTime?: string, label?: string) => void;
  defaultRange?: string;
  addTime?: boolean; // Optional prop to add time to the date
};

const DateRangeSelectorButton: React.FC<Props> = ({
  onDateRangeChange,
  defaultRange = "Today",
  addTime = false, // Default to false if not provided
}) => {
  const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null);
  const [calendarAnchorEl, setCalendarAnchorEl] = useState<null | HTMLElement>(null);
  const [showDatePicker, setShowDatePicker] = useState(false);
  const [selectedDuration, setSelectedDuration] = useState<string>(defaultRange);
  const [startTime, setStartTime] = useState<string>("00:00:00");
  const [endTime, setEndTime] = useState<string>("23:59:59");
  const initialRange = getDateRange(defaultRange, addTime);
  const [startDate, setStartDate] = useState<string>(getDateRange("Today", addTime).startDate);
  const [endDate, setEndDate] = useState<string>(getDateRange("Today", addTime).endDate);
  const [errorMsg, setErrorMsg] = useState<string | null>(null);
  const handleClick = (event: React.MouseEvent<HTMLButtonElement>) => {
    setAnchorEl(event.currentTarget);
  };

  const handleClose = () => setAnchorEl(null);

  const handleMenuItemClick = (label: string, event: React.MouseEvent<HTMLElement>) => {

    if (label === "Custom Range") {
      setCalendarAnchorEl(event.currentTarget);
      setShowDatePicker(true);
      return;
    }
    setAnchorEl(null);
    setSelectedDuration(label);

    const result = getDateRange(label, addTime);
    const { startDate, endDate } = result;
    const from = startDate;
    const to = endDate;

    setStartDate(from);
    setEndDate(to);
    if (addTime) {
      const { fromTime, toTime } = result as {
        startDate: string;
        endDate: string;
        fromTime: string;
        toTime: string;
      };

      onDateRangeChange?.(from, to, fromTime, toTime, label);
    } else {
      onDateRangeChange?.(from, to, label);
    }
  };

  const handleCalendarClose = () => {
    setAnchorEl(null);
    setErrorMsg("");
    setShowDatePicker(false);
    setCalendarAnchorEl(null);
    
  };

  const handleApplyCustomRange = (from: string, to: string, fromTime?: string, toTime?: string) => {

    const now = dayjs();
    const fromDateTime = dayjs(`${from}T${fromTime || "00:00:00"}`);
    const toDateTime = dayjs(`${to}T${toTime || "23:59:59"}`);

    if (fromDateTime.isAfter(now) || toDateTime.isAfter(now)) {
      setErrorMsg("Future dates & time are not allowed");
     // setTimeout(() => setErrorMsg(""), 3000); // Hide after 3s
      return;
    }

    setAnchorEl(null);
    setErrorMsg("");

    setStartDate(from);
    setEndDate(to);
    setStartTime(fromTime || "00:00:00");
    setEndTime(toTime || "23:59:59");
    setSelectedDuration("Custom Range");
    setShowDatePicker(false);
    setCalendarAnchorEl(null);
    onDateRangeChange?.(from, to, fromTime, toTime);

  };

  return (
    <div className="relative">
      <Button
        onClick={handleClick}
        variant="outlined"
        sx={{
          height: { xs: "28px", sm: "30px", md: "48px" },
          px: { xs: 1, sm: 2 },
          borderRadius: "8px",
          textTransform: "none",
          fontWeight: 700,
          display: "inline-flex",
          alignItems: "center",
          gap: { xs: 0.5, sm: 1 },
          borderColor: "#5547dd",
          backgroundColor: "white",
          color: "#5547dd",
          minWidth: 0,
          maxWidth: "100%",
        }}
      >
        <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
          <img src={calendar} alt="Calendar" width={18} height={18} />
          <Typography
            variant="body2"
            sx={{
              whiteSpace: "nowrap",
              overflow: "hidden",
              textOverflow: "ellipsis",
              maxWidth: "100%",
            }}
          >
            {selectedDuration}
          </Typography>
          <img src={arrow} alt="Arrow" width={18} height={18} />
        </Box>
      </Button>

      <Menu
        anchorEl={anchorEl}
        open={Boolean(anchorEl)}
        onClose={handleClose}
        PaperProps={{
          sx: {
            borderRadius: "8px",
            boxShadow: "0 6px 10px rgba(0, 0, 0, 0.1)",
          },
        }}
      >
        {dateRangeArr.map((label: string) => (
          <MenuItem
            key={label}
            onClick={(e) => handleMenuItemClick(label, e)}
            selected={selectedDuration === label}
          >
            <Typography variant="button" textTransform={"none"}>
              {label}
            </Typography>
          </MenuItem>
        ))}
      </Menu>

      <Menu
        anchorEl={calendarAnchorEl}
        open={showDatePicker}
        onClose={handleCalendarClose}
        slotProps={{
          paper: {
            sx: {
              borderRadius: "8px",
              padding: 0,
              boxShadow: "0 6px 10px rgba(0, 0, 0, 0.1)",
            },
          },
        }}
      >
        <DateRangeFilter
          onCancel={handleCalendarClose}
          onApply={handleApplyCustomRange}
          initialFrom={startDate}
          initialTo={endDate}
          addTime={addTime} // Pass the addTime prop to DateRangeFilter
          initialFromTime={addTime ? startTime : undefined}
          initialToTime={addTime ? endTime : undefined}
        />
        {errorMsg && (
          <Box sx={{ padding: 0, color: "red", textAlign: "center" }}>
            <Typography variant="body2">{errorMsg}</Typography>
          </Box>
        )}
      </Menu>
      
    </div>
  );
};

export default DateRangeSelectorButton;
